import React, { useRef, useState } from 'react';
import { validateExcelFile } from '../utils/excelUtils';
import ErrorDialog from './ErrorDialog';
import ExcelStructurePreview from './ExcelStructurePreview';
import SystemInfo from './SystemInfo';
import '../styles/FileUpload.css';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showError, setShowError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (!validateExcelFile(file)) {
      setShowError(true);
      return;
    }
    onFileUpload(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload-wrapper">
      <SystemInfo />
      
      <div className="file-upload-container">
        <div
          className={`upload-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <div className="upload-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        
        <h3 className="upload-title">Ανέβασμα Αρχείου Excel</h3>
        <p className="upload-description">
          Σύρετε και αφήστε το αρχείο Excel εδώ, ή κάντε κλικ για ανέβασμα
        </p>
        
        <div className="upload-requirements">
          <div className="requirement">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Μόνο μορφή .xlsx</span>
          </div>
          <div className="requirement">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>Υποστηρίζεται drag & drop (σύρετε και αφήστε)</span>
          </div>
          <div className="requirement suggestion">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>Συνιστάται μέχρι 300 οχήματα (όχι υποχρεωτικό)</span>
          </div>
          <div className="requirement">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Απαιτείται ΑΦΜ & Αριθμός Κυκλοφορίας</span>
          </div>
        </div>
        
        <button className="browse-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Ανέβασμα Αρχείου
        </button>
        </div>

        <ExcelStructurePreview />
      </div>

      {showError && (
        <ErrorDialog
          title="Μη Έγκυρο Αρχείο"
          message="Παρακαλώ ανεβάστε ένα έγκυρο αρχείο Excel (.xlsx). Το αρχείο πρέπει να είναι σε μορφή .xlsx και να περιέχει τα απαιτούμενα δεδομένα (ΑΦΜ και Αριθμός Κυκλοφορίας)."
          onClose={() => setShowError(false)}
        />
      )}
    </div>
  );
};

export default FileUpload;
